(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function loadDeferredVideo(video) {
    if (!video || video.dataset.fineArtsVideoLoaded === 'true') return Promise.resolve(video);

    const src = video.dataset.src;
    if (!src) return Promise.resolve(video);

    video.dataset.fineArtsVideoLoaded = 'true';
    video.src = src;
    video.load();

    return new Promise((resolve) => {
      const onReady = () => {
        video.classList.add('is-ready');
        resolve(video);
      };

      if (video.readyState >= 2) {
        onReady();
        return;
      }

      video.addEventListener('loadeddata', onReady, { once: true });
      video.addEventListener('error', () => resolve(video), { once: true });
    });
  }

  function playDeferredVideo(video) {
    if (!video) return;
    const shouldAutoplay =
      video.hasAttribute('data-fine-arts-video-autoplay') ||
      video.dataset.fineArtsVideoPriority === 'high' ||
      video.hasAttribute('autoplay');

    if (!shouldAutoplay || reduceMotion) return;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }

  function initDeferredVideos() {
    const videos = Array.from(document.querySelectorAll('video[data-fine-arts-video][data-src]'));
    if (!videos.length) return;

    const highPriority = videos.filter((video) => video.dataset.fineArtsVideoPriority === 'high');
    const lazyVideos = videos.filter((video) => video.dataset.fineArtsVideoPriority !== 'high');

    const activate = (video) => {
      loadDeferredVideo(video).then(playDeferredVideo);
    };

    // Hero: wait until window load so the poster can become LCP first.
    const startHero = () => {
      highPriority.forEach((video) => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => activate(video), { timeout: 1200 });
        } else {
          setTimeout(() => activate(video), 200);
        }
      });
    };

    if (document.readyState === 'complete') {
      startHero();
    } else {
      window.addEventListener('load', startHero, { once: true });
    }

    if (!lazyVideos.length) return;

    if (!('IntersectionObserver' in window)) {
      lazyVideos.forEach(activate);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const video = entry.target;
          observer.unobserve(video);
          activate(video);
        }
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    );

    lazyVideos.forEach((video) => observer.observe(video));
  }

  function initReveal() {
    if (reduceMotion) return;

    const revealSelector = '.fine-arts-reveal';
    const threshold = 0.12;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin: '0px 0px -8% 0px' }
    );

    function observeAll() {
      document.querySelectorAll(revealSelector).forEach((el) => {
        if (!el.classList.contains('is-visible')) observer.observe(el);
      });

      document.querySelectorAll('.section-resource-list').forEach((el) => {
        el.classList.add('fine-arts-reveal');
        if (!el.classList.contains('is-visible')) observer.observe(el);
      });
    }

    observeAll();
    document.addEventListener('shopify:section:load', observeAll);
  }

  function initParallax() {
    if (reduceMotion) return;

    const sections = document.querySelectorAll('.fine-arts-parallax-media');
    if (!sections.length) return;

    let ticking = false;

    const update = () => {
      const viewHeight = window.innerHeight;

      sections.forEach((section) => {
        const media = section.querySelector('video, .resource-image__image, .media-block__media');
        if (!media) return;

        const rect = section.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewHeight) return;

        const progress = (viewHeight - rect.top) / (viewHeight + rect.height);
        const offset = (progress - 0.5) * 28;
        media.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0) scale(1.05)`;
      });

      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  function initHeaderScroll() {
    const header = document.querySelector('header-component');
    if (!header) return;

    const threshold = 72;
    let ticking = false;

    const update = () => {
      header.toggleAttribute('data-fine-arts-scrolled', window.scrollY > threshold);
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  function init() {
    initDeferredVideos();
    initReveal();
    initParallax();
    initHeaderScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', initDeferredVideos);
})();

(function applyFineArtsFooterLayout() {
  if (document.getElementById('fine-arts-footer-layout-fix')) return;

  const style = document.createElement('style');
  style.id = 'fine-arts-footer-layout-fix';
  style.textContent = `
    footer { background: rgb(250 248 245 / 0.98); }
    .fine-arts-local-nap, .fine-arts-footer { border-top: 1px solid rgb(44 36 22 / 0.1); background: transparent; }
    .fine-arts-local-nap__inner, .fine-arts-footer__inner {
      width: min(72rem, calc(100% - var(--page-margin, 16px) * 2));
      margin-inline: auto;
      padding-inline: var(--page-margin, 16px);
      box-sizing: content-box;
    }
    @media screen and (min-width: 900px) {
      .fine-arts-local-nap__inner {
        grid-template-columns: minmax(0, 1fr) min(340px, 38%) !important;
        align-items: start !important;
        gap: 28px !important;
      }
    }
    footer .shopify-section-group-footer-group .footer-content {
      width: min(72rem, calc(100% - var(--page-margin, 16px) * 2));
      margin-inline: auto;
      padding-inline: var(--page-margin, 16px);
      box-sizing: border-box;
    }
    @media screen and (min-width: 900px) {
      footer .shopify-section-group-footer-group .footer-content {
        grid-template-columns: minmax(0, 1fr) minmax(280px, 420px) !important;
        align-items: center;
        gap: 24px;
      }
    }
    footer .footer-utilities .utilities {
      width: min(72rem, calc(100% - var(--page-margin, 16px) * 2));
      margin-inline: auto;
      padding-inline: var(--page-margin, 16px);
      box-sizing: border-box;
    }
    @media screen and (min-width: 750px) {
      footer .footer-utilities .utilities--blocks-3 {
        grid-template-columns: minmax(0, 1fr) auto auto !important;
      }
      footer .footer-utilities .utilities--blocks-3 > *:nth-child(2),
      footer .footer-utilities .utilities--blocks-3 > *:nth-child(3) {
        justify-self: end;
        text-align: right;
      }
    }
    .fine-arts-footer ~ .shopify-section-group-footer-group { display: none !important; }
  `;
  document.head.appendChild(style);
})();
